import type { FilingProvider } from '../api/tax-filing';

export interface TaxSourceProofSnapshot {
  ownerSurface: '/transactions';
  sourceLabel: string;
  importedAt: string | null;
  importedCount: number | null;
  skippedCount: number | null;
  errorCount: number | null;
  matchedCount: number | null;
  year: number | null;
  provider: FilingProvider | null;
  sourceKind?: 'csv-import' | 'api-sync';
  freshnessState?: 'healthy' | 'stale_verified' | 'never_synced' | null;
  freshnessStaleHours?: number | null;
  blockerCode?: string | null;
  blockerClassification?: 'safe_exchange_replay_blocked' | 'credential_action' | 'auth_action' | null;
  runnerWaitState?: 'runner_limited_wait' | null;
  blockerDetail?: string | null;
}

function getStorageKey(userId: string): string {
  return `pinecurl.tax-source-proof:${userId}`;
}

function isFilingProvider(value: unknown): value is FilingProvider {
  return value === 'turbotax' || value === 'hmrc' || value === 'csv';
}

function toNullableNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function isTaxSourceProofSnapshot(value: unknown): value is TaxSourceProofSnapshot {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<TaxSourceProofSnapshot>;
  return (
    candidate.ownerSurface === '/transactions'
    && typeof candidate.sourceLabel === 'string'
    && candidate.sourceLabel.trim().length > 0
  );
}

function readStorageValue(storage: Storage | null, key: string): TaxSourceProofSnapshot | null {
  if (!storage) {
    return null;
  }

  try {
    const raw = storage.getItem(key);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    return isTaxSourceProofSnapshot(parsed)
      ? {
        ownerSurface: '/transactions',
        sourceLabel: parsed.sourceLabel,
        importedAt: typeof parsed.importedAt === 'string' ? parsed.importedAt : null,
        importedCount: toNullableNumber(parsed.importedCount),
        skippedCount: toNullableNumber(parsed.skippedCount),
        errorCount: toNullableNumber(parsed.errorCount),
        matchedCount: toNullableNumber(parsed.matchedCount),
        year: toNullableNumber(parsed.year),
        provider: isFilingProvider(parsed.provider) ? parsed.provider : null,
        sourceKind: parsed.sourceKind === 'api-sync' ? 'api-sync' : parsed.sourceKind === 'csv-import' ? 'csv-import' : undefined,
        freshnessState:
          parsed.freshnessState === 'healthy' || parsed.freshnessState === 'stale_verified' || parsed.freshnessState === 'never_synced'
            ? parsed.freshnessState
            : null,
        freshnessStaleHours: toNullableNumber(parsed.freshnessStaleHours),
        blockerCode: typeof parsed.blockerCode === 'string' ? parsed.blockerCode : null,
        blockerClassification:
          parsed.blockerClassification === 'safe_exchange_replay_blocked'
          || parsed.blockerClassification === 'credential_action'
          || parsed.blockerClassification === 'auth_action'
            ? parsed.blockerClassification
            : null,
        runnerWaitState: parsed.runnerWaitState === 'runner_limited_wait' ? 'runner_limited_wait' : null,
        blockerDetail: typeof parsed.blockerDetail === 'string' ? parsed.blockerDetail : null,
      }
      : null;
  } catch {
    return null;
  }
}

export function readPersistedTaxSourceProof(userId?: string): TaxSourceProofSnapshot | null {
  if (typeof window === 'undefined' || !userId) {
    return null;
  }

  const key = getStorageKey(userId);
  return (
    readStorageValue(window.sessionStorage, key)
    ?? readStorageValue(window.localStorage, key)
  );
}

export function persistTaxSourceProof(userId: string | undefined, proof: TaxSourceProofSnapshot | null): void {
  if (typeof window === 'undefined' || !userId) {
    return;
  }

  const key = getStorageKey(userId);

  try {
    if (!proof) {
      window.sessionStorage.removeItem(key);
      window.localStorage.removeItem(key);
      return;
    }

    const serialised = JSON.stringify(proof);
    window.sessionStorage.setItem(key, serialised);
    window.localStorage.setItem(key, serialised);
  } catch {
    // noop
  }
}

export function appendTaxSourceProofParams(
  params: URLSearchParams,
  proof: TaxSourceProofSnapshot | null,
): URLSearchParams {
  if (!proof) {
    return params;
  }

  params.set('sourceLabel', proof.sourceLabel);

  if (proof.importedAt) {
    params.set('importedAt', proof.importedAt);
  }
  if (proof.importedCount !== null) {
    params.set('importedCount', String(proof.importedCount));
  }
  if (proof.skippedCount !== null) {
    params.set('skippedCount', String(proof.skippedCount));
  }
  if (proof.errorCount !== null) {
    params.set('errorCount', String(proof.errorCount));
  }
  if (proof.matchedCount !== null) {
    params.set('matchedCount', String(proof.matchedCount));
  }
  if (proof.sourceKind) {
    params.set('sourceKind', proof.sourceKind);
  }
  if (proof.freshnessState) {
    params.set('freshnessState', proof.freshnessState);
  }
  if (proof.freshnessStaleHours !== null && proof.freshnessStaleHours !== undefined) {
    params.set('freshnessStaleHours', String(proof.freshnessStaleHours));
  }
  if (proof.blockerCode) {
    params.set('blockerCode', proof.blockerCode);
  }
  if (proof.blockerClassification) {
    params.set('blockerClassification', proof.blockerClassification);
  }
  if (proof.runnerWaitState) {
    params.set('runnerWaitState', proof.runnerWaitState);
  }
  if (proof.blockerDetail) {
    params.set('blockerDetail', proof.blockerDetail);
  }

  return params;
}

export function readTaxSourceProofFromSearchParams(
  searchParams: URLSearchParams,
  fallback?: {
    year?: number | null;
    provider?: FilingProvider | null;
  },
): TaxSourceProofSnapshot | null {
  const sourceLabel = searchParams.get('sourceLabel');
  if (!sourceLabel) {
    return null;
  }

  return {
    ownerSurface: '/transactions',
    sourceLabel,
    importedAt: searchParams.get('importedAt'),
    importedCount: toNullableNumber(searchParams.get('importedCount')),
    skippedCount: toNullableNumber(searchParams.get('skippedCount')),
    errorCount: toNullableNumber(searchParams.get('errorCount')),
    matchedCount: toNullableNumber(searchParams.get('matchedCount')),
    year: fallback?.year ?? null,
    provider: fallback?.provider ?? null,
    sourceKind: searchParams.get('sourceKind') === 'api-sync' ? 'api-sync' : searchParams.get('sourceKind') === 'csv-import' ? 'csv-import' : undefined,
    freshnessState:
      searchParams.get('freshnessState') === 'healthy'
      || searchParams.get('freshnessState') === 'stale_verified'
      || searchParams.get('freshnessState') === 'never_synced'
        ? (searchParams.get('freshnessState') as 'healthy' | 'stale_verified' | 'never_synced')
        : null,
    freshnessStaleHours: toNullableNumber(searchParams.get('freshnessStaleHours')),
    blockerCode: searchParams.get('blockerCode'),
    blockerClassification:
      searchParams.get('blockerClassification') === 'safe_exchange_replay_blocked'
      || searchParams.get('blockerClassification') === 'credential_action'
      || searchParams.get('blockerClassification') === 'auth_action'
        ? (searchParams.get('blockerClassification') as 'safe_exchange_replay_blocked' | 'credential_action' | 'auth_action')
        : null,
    runnerWaitState: searchParams.get('runnerWaitState') === 'runner_limited_wait' ? 'runner_limited_wait' : null,
    blockerDetail: searchParams.get('blockerDetail'),
  };
}
